import { useMemo } from "react";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { getCsrfHeader } from "../lib/csrf";
import { useCachedQuery, type CachedQueryStatus } from "./useCachedQuery";
import type { MutationResult, Tier } from "../types/billing";

// Tier catalog + per-tier pricing for the admin "Tiers & Pricing" tab. The list
// seeds from the shared admin:tiers cache (SWR pattern); mutations write through
// the admin API then reload so the grid reflects the server.

const URL = ADMIN_QUERIES.tiers.url; // /api/admin/billing/tiers

export type TierUpsert = {
  readonly tierCode?: string;
  readonly displayName: string;
  readonly rank: number;
  readonly includedSlots: number;
  readonly includedCoachingSessions: number;
  readonly active: boolean;
  readonly trialDays: number;
  // Checkout copy for the tier's plan / setup-fee items. Omitted names fall
  // back server-side (create) or stay untouched (edit); descriptions are always
  // sent so an emptied field clears the stored copy.
  readonly planName?: string;
  readonly planDescription?: string;
  readonly setupFeeName?: string;
  readonly setupFeeDescription?: string;
};

export type PricingUpsert = {
  readonly unitAmountCents: number;
  readonly currency: string;
  readonly included: boolean;
};

const send = async (input: string, init: RequestInit): Promise<MutationResult> => {
  try {
    const res = await fetch(input, {
      credentials: "include",
      ...init,
      headers: { "Content-Type": "application/json", ...(init.headers ?? {}), ...getCsrfHeader() },
    });
    if (res.ok) return { ok: true };
    let message = `request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string; error?: string };
      message = body.message ?? body.error ?? message;
    } catch {
      // non-JSON body; keep the status message
    }
    return { ok: false, message };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : "network error" };
  }
};

export type UseAdminTiersResult = {
  readonly status: CachedQueryStatus;
  readonly tiers: Tier[];
  readonly error: string | null;
  readonly isFetching: boolean;
  readonly refresh: () => void;
  readonly createTier: (body: TierUpsert) => Promise<MutationResult>;
  readonly updateTier: (tierCode: string, body: TierUpsert) => Promise<MutationResult>;
  readonly deleteTier: (tierCode: string) => Promise<MutationResult>;
  readonly setPricing: (
    tierCode: string,
    itemCode: string,
    interval: string,
    body: PricingUpsert,
  ) => Promise<MutationResult>;
};

export const useAdminTiers = (): UseAdminTiersResult => {
  const fetcher = useMemo(
    () => async (signal: AbortSignal): Promise<Tier[]> => {
      const res = await fetch(URL, { credentials: "include", signal });
      if (!res.ok) throw new Error(`fetch failed (${res.status})`);
      return (await res.json()) as Tier[];
    },
    [],
  );

  const { status, data, error, isFetching, refetch } = useCachedQuery<Tier[]>({
    cacheResource: ADMIN_QUERIES.tiers.resource,
    queryKey: URL,
    fetcher,
  });

  const reload = (): void => void refetch();
  const run = async (input: string, init: RequestInit): Promise<MutationResult> => {
    const result = await send(input, init);
    if (result.ok) reload();
    return result;
  };

  return {
    status,
    tiers: data ?? [],
    error,
    isFetching,
    refresh: reload,
    createTier: (body) => run(URL, { method: "POST", body: JSON.stringify(body) }),
    updateTier: (tierCode, body) =>
      run(`${URL}/${encodeURIComponent(tierCode)}`, { method: "PUT", body: JSON.stringify(body) }),
    deleteTier: (tierCode) => run(`${URL}/${encodeURIComponent(tierCode)}`, { method: "DELETE" }),
    setPricing: (tierCode, itemCode, interval, body) =>
      run(
        `${URL}/${encodeURIComponent(tierCode)}/pricing/${encodeURIComponent(itemCode)}/${encodeURIComponent(interval)}`,
        { method: "PUT", body: JSON.stringify(body) },
      ),
  };
};
